/**
 * Vendor Detail Page
 * 
 * Displays vendor information, stats, linked contact, related POs and Bills
 */
import React, { useState, useEffect, useCallback } from 'react';
import { vendorsAPI, purchaseOrdersAPI, vendorBillsAPI, crmAPI } from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { useRefetchOnReturn } from '../../hooks';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import {
  Building2,
  Phone,
  Mail,
  FileText,
  ShoppingBag,
  DollarSign,
  Plus,
  Loader2,
  ArrowLeft,
  Edit2,
  User,
  Link as LinkIcon,
  Unlink,
  CheckCircle,
  XCircle,
  Clock,
  Eye,
  ExternalLink,
  RefreshCw,
  Trash2,
  Star,
  Users,
} from 'lucide-react';
import ChecksSection from '../../components/admin/ChecksSection';

// Vendor types
const VENDOR_TYPES = [
  { value: 'awards', label: 'Awards' },
  { value: 'printing', label: 'Printing' },
  { value: 'large_format', label: 'Large Format' },
  { value: 'promo', label: 'Promo' },
  { value: 'packaging', label: 'Packaging' },
  { value: 'shipping', label: 'Shipping' },
  { value: 'services', label: 'Services' },
  { value: 'other', label: 'Other' },
];

// Payment terms
const PAYMENT_TERMS = [
  { value: 'NET_30', label: 'Net 30' },
  { value: 'NET_15', label: 'Net 15' },
  { value: 'DUE_ON_RECEIPT', label: 'Due on Receipt' },
  { value: 'PREPAID', label: 'Prepaid' },
];

// Format currency
const formatCurrency = (cents) => {
  if (cents === null || cents === undefined) return '$0.00';
  return (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};

// Format date
const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

// Status badge colors
const PO_STATUS_COLORS = {
  draft: 'bg-gray-100 text-gray-700',
  sent: 'bg-blue-100 text-blue-700',
  partial: 'bg-yellow-100 text-yellow-700',
  closed: 'bg-green-100 text-green-700',
  cancelled: 'bg-red-100 text-red-700',
};

const BILL_STATUS_COLORS = {
  draft: 'bg-gray-100 text-gray-700',
  unpaid: 'bg-yellow-100 text-yellow-700',
  paid: 'bg-green-100 text-green-700',
  void: 'bg-gray-100 text-gray-500',
};

// Contact roles
const CONTACT_ROLES = [
  { value: 'primary', label: 'Primary Contact' },
  { value: 'sales_rep', label: 'Sales Rep' },
  { value: 'accounts_payable', label: 'Accounts Payable' },
  { value: 'shipping', label: 'Shipping' },
  { value: 'other', label: 'Other' },
];

const VendorDetailPage = ({ 
  vendorId, 
  onBack, 
  onNavigateToPO,
  onNavigateToBill,
  onCreatePO,
  onCreateBill 
}) => {
  const { isAdmin } = useAuth();
  
  // Data state
  const [vendor, setVendor] = useState(null);
  const [stats, setStats] = useState(null);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  
  // Contact linking state
  const [contacts, setContacts] = useState([]);
  const [contactSearch, setContactSearch] = useState('');
  const [showContactPicker, setShowContactPicker] = useState(false);
  
  // Vendor contacts modal state
  const [showContactModal, setShowContactModal] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [contactFormData, setContactFormData] = useState({
    name: '',
    email: '',
    phone: '',
    role: 'primary',
    is_primary: false,
    notes: ''
  });
  const [savingContact, setSavingContact] = useState(false);
  
  // Fetch vendor data
  const fetchVendor = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const data = await vendorsAPI.getVendor(vendorId);
      // API returns vendor directly, not wrapped
      setVendor(data);
      
      // Fetch stats separately if needed
      try {
        const statsData = await vendorsAPI.getVendorStats(vendorId);
        setStats(statsData);
      } catch {
        // Stats endpoint may not exist, set defaults
        setStats({
          total_po_count: 0,
          total_po_amount: 0,
          total_bill_count: 0,
          total_bill_amount: 0,
          total_paid: 0,
          balance_due: 0
        });
      }
      
      // Fetch related POs (last 5)
      const posData = await purchaseOrdersAPI.getPurchaseOrders({
        vendor_id: vendorId,
        page_size: 5,
        sort_by: 'created_at',
        sort_dir: 'desc'
      });
      setPurchaseOrders(posData.purchase_orders || posData.items || []);
      
      // Fetch related Bills (last 5)
      const billsData = await vendorBillsAPI.getBills({
        vendor_id: vendorId,
        page_size: 5,
        sort_by: 'created_at',
        sort_dir: 'desc'
      });
      setBills(billsData.bills || billsData.items || []);
      
    } catch (err) {
      setError(err.message || 'Failed to load vendor');
      toast.error(err.message || 'Failed to load vendor');
    } finally {
      setLoading(false);
    }
  }, [vendorId]);
  
  useEffect(() => {
    if (vendorId) {
      fetchVendor();
    }
  }, [vendorId, fetchVendor]);
  
  // Handle manual refresh
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await fetchVendor();
      toast.success('Data refreshed');
    } finally {
      setRefreshing(false);
    }
  };

  // Auto-refetch on tab focus or route return
  useRefetchOnReturn({
    refetch: fetchVendor,
    enabled: !loading && !!vendorId,
    refreshing,
    throttleMs: 1500,
  });
  
  // Open edit modal
  const openEditModal = () => {
    setEditData({
      name: vendor?.name || '',
      vendor_type: vendor?.vendor_type || 'other',
      email: vendor?.email || '',
      phone: vendor?.phone || '',
      payment_terms: vendor?.payment_terms || '',
      notes: vendor?.notes || '',
      // Contact fields
      primary_contact_id: vendor?.primary_contact_id || '',
      contact_name: vendor?.contact_name || '',
      contact_email: vendor?.contact_email || '',
      contact_phone: vendor?.contact_phone || '',
    });
    setShowEditModal(true);
  };
  
  // Search contacts for linking
  const searchContacts = async (query) => {
    if (query.length < 2) {
      setContacts([]);
      return;
    }
    try {
      const data = await crmAPI.getContacts({ q: query, page_size: 10 });
      setContacts(data.contacts || []);
    } catch (err) {
      console.error('Failed to search contacts:', err);
    }
  };
  
  // Handle contact selection
  const handleSelectContact = (contact) => {
    setEditData(prev => ({
      ...prev,
      primary_contact_id: contact.id,
      contact_name: `${contact.first_name || ''} ${contact.last_name || ''}`.trim(),
      contact_email: contact.email || '',
      contact_phone: contact.phone || '',
    }));
    setShowContactPicker(false);
    setContactSearch('');
    setContacts([]);
  };
  
  // Clear linked contact (switch to manual)
  const handleUnlinkContact = () => {
    setEditData(prev => ({
      ...prev,
      primary_contact_id: '',
      // Keep manual fields editable
    }));
  };
  
  // Save vendor
  const handleSave = async () => {
    if (!editData.name?.trim()) {
      toast.error('Vendor name is required');
      return;
    }
    
    setSaving(true);
    try {
      const updatePayload = {
        name: editData.name,
        vendor_type: editData.vendor_type,
        email: editData.email || null,
        phone: editData.phone || null,
        payment_terms: editData.payment_terms || null,
        notes: editData.notes || null,
        // Contact fields - send based on whether linked or manual
        primary_contact_id: editData.primary_contact_id || null,
        contact_name: editData.primary_contact_id ? null : (editData.contact_name || null),
        contact_email: editData.primary_contact_id ? null : (editData.contact_email || null),
        contact_phone: editData.primary_contact_id ? null : (editData.contact_phone || null),
      };
      
      await vendorsAPI.updateVendor(vendorId, updatePayload);
      toast.success('Vendor updated');
      setShowEditModal(false);
      fetchVendor();
    } catch (err) {
      toast.error(err.message || 'Failed to update vendor');
    } finally {
      setSaving(false);
    }
  };
  
  // Toggle active status
  const handleToggleActive = async () => {
    try {
      if (vendor.is_active) {
        await vendorsAPI.deactivateVendor(vendorId);
        toast.success('Vendor deactivated');
      } else {
        await vendorsAPI.reactivateVendor(vendorId);
        toast.success('Vendor reactivated');
      }
      fetchVendor();
    } catch (err) {
      toast.error(err.message || 'Failed to update vendor status');
    }
  };
  
  // ============================================
  // VENDOR CONTACTS HANDLERS
  // ============================================
  
  const openAddContactModal = () => {
    setEditingContact(null);
    setContactFormData({
      name: '',
      email: '',
      phone: '',
      role: 'primary',
      is_primary: (vendor?.contacts || []).length === 0, // First contact is primary
      notes: ''
    });
    setShowContactModal(true);
  };
  
  const openEditContactModal = (contact) => {
    setEditingContact(contact);
    setContactFormData({
      name: contact.name || '',
      email: contact.email || '',
      phone: contact.phone || '',
      role: contact.role || 'primary',
      is_primary: contact.is_primary || false,
      notes: contact.notes || ''
    });
    setShowContactModal(true);
  };
  
  const handleSaveContact = async () => {
    if (!contactFormData.name?.trim()) {
      toast.error('Contact name is required');
      return;
    }
    
    setSavingContact(true);
    try {
      if (editingContact) {
        await vendorsAPI.updateContact(vendorId, editingContact.id, contactFormData);
        toast.success('Contact updated');
      } else {
        await vendorsAPI.addContact(vendorId, contactFormData);
        toast.success('Contact added');
      }
      setShowContactModal(false);
      fetchVendor();
    } catch (err) {
      toast.error(err.message || 'Failed to save contact');
    } finally {
      setSavingContact(false);
    }
  };
  
  const handleDeleteContact = async (contact) => {
    if (!window.confirm(`Delete contact "${contact.name}"?`)) return;
    
    try {
      await vendorsAPI.deleteContact(vendorId, contact.id);
      toast.success('Contact deleted');
      fetchVendor();
    } catch (err) {
      toast.error(err.message || 'Failed to delete contact');
    }
  };
  
  const handleSetPrimaryContact = async (contact) => {
    try {
      await vendorsAPI.setPrimaryContact(vendorId, contact.id);
      toast.success(`${contact.name} is now the primary contact`);
      fetchVendor();
    } catch (err) {
      toast.error(err.message || 'Failed to set primary contact');
    }
  };
  
  // Get primary contact for display
  const primaryContact = (vendor?.contacts || []).find(c => c.is_primary);
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }
  
  if (error || !vendor) {
    return (
      <div className="p-6">
        <Button variant="ghost" onClick={onBack} className="mb-4">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Vendors
        </Button>
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error || 'Vendor not found'}
        </div>
      </div>
    );
  }
  
  // Determine contact display
  const contactDisplay = vendor.primary_contact_id 
    ? vendor.primary_contact_snapshot 
    : { name: vendor.contact_name, email: vendor.contact_email, phone: vendor.contact_phone };
  
  return (
    <div className="space-y-6" data-testid="vendor-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={onBack} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{vendor.name}</h1>
                <p className="text-sm text-gray-500 font-mono" data-testid="vendor-id">{vendor.id}</p>
                <div className="flex items-center gap-2 mt-1">
                  <Badge className={vendor.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}>
                    {vendor.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                  <Badge className="bg-blue-50 text-blue-700 capitalize">
                    {vendor.vendor_type?.replace('_', ' ') || 'Other'}
                  </Badge>
                  {vendor.payment_terms && (
                    <Badge variant="outline">
                      {PAYMENT_TERMS.find(t => t.value === vendor.payment_terms)?.label || vendor.payment_terms}
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={handleRefresh}
            disabled={refreshing}
            data-testid="refresh-vendor-btn"
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          </Button>
          <Button variant="outline" onClick={handleToggleActive} data-testid="toggle-active-btn">
            {vendor.is_active ? (
              <><XCircle className="w-4 h-4 mr-2" />Deactivate</>
            ) : (
              <><CheckCircle className="w-4 h-4 mr-2" />Reactivate</>
            )}
          </Button>
          <Button onClick={openEditModal} data-testid="edit-vendor-btn">
            <Edit2 className="w-4 h-4 mr-2" />
            Edit
          </Button>
        </div>
      </div>
      
      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Contacts Card */}
          <div className="bg-white rounded-xl border p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <Users className="w-5 h-5 text-gray-400" />
                Contacts
                {(vendor.contacts || []).length > 0 && (
                  <Badge variant="outline">{(vendor.contacts || []).length}</Badge>
                )}
              </h2>
              <Button size="sm" onClick={openAddContactModal} data-testid="add-contact-btn">
                <Plus className="w-4 h-4 mr-1" />
                Add Contact
              </Button>
            </div>
            
            {(vendor.contacts || []).length > 0 ? (
              <div className="space-y-3">
                {(vendor.contacts || []).map(contact => (
                  <div 
                    key={contact.id} 
                    className={`p-4 rounded-lg border ${contact.is_primary ? 'border-amber-300 bg-amber-50' : 'border-gray-200 bg-gray-50'}`}
                    data-testid={`contact-${contact.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          {contact.is_primary && (
                            <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                          )}
                          <span className="font-medium">{contact.name}</span>
                          <Badge variant="outline" className="text-xs">
                            {CONTACT_ROLES.find(r => r.value === contact.role)?.label || contact.role}
                          </Badge>
                        </div>
                        <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                          {contact.email && (
                            <a href={`mailto:${contact.email}`} className="flex items-center gap-1 hover:text-blue-600">
                              <Mail className="w-3 h-3" />
                              {contact.email}
                            </a>
                          )}
                          {contact.phone && (
                            <a href={`tel:${contact.phone}`} className="flex items-center gap-1 hover:text-blue-600">
                              <Phone className="w-3 h-3" />
                              {contact.phone}
                            </a>
                          )}
                        </div>
                        {contact.notes && (
                          <p className="text-xs text-gray-500 mt-1">{contact.notes}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        {!contact.is_primary && (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleSetPrimaryContact(contact)}
                            title="Set as Primary"
                          >
                            <Star className="w-4 h-4" />
                          </Button>
                        )}
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => openEditContactModal(contact)}
                          data-testid={`edit-contact-${contact.id}`}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleDeleteContact(contact)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <User className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No contacts added yet</p>
                <p className="text-sm">Add your primary contact and sales rep</p>
              </div>
            )}
          </div>
          
          {/* Vendor Info Card */}
          <div className="bg-white rounded-xl border p-6">
            <h2 className="text-lg font-semibold mb-4">Vendor Information</h2>
            <div className="grid grid-cols-2 gap-6">
              {/* General Contact (vendor-level email/phone) */}
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-2">General Contact</h3>
                <div className="space-y-1">
                  {vendor.email && (
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-gray-400" />
                      <a href={`mailto:${vendor.email}`} className="text-blue-600 hover:underline">
                        {vendor.email}
                      </a>
                    </div>
                  )}
                  {vendor.phone && (
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-gray-400" />
                      <a href={`tel:${vendor.phone}`} className="text-blue-600 hover:underline">
                        {vendor.phone}
                      </a>
                    </div>
                  )}
                  {!vendor.email && !vendor.phone && (
                    <span className="text-gray-400">—</span>
                  )}
                </div>
              </div>
              
              {/* Payment Terms */}
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-2">Payment Terms</h3>
                <span className="text-gray-700">
                  {PAYMENT_TERMS.find(t => t.value === vendor.payment_terms)?.label || vendor.payment_terms || '—'}
                </span>
              </div>
            </div>
            
            {/* Notes */}
            {vendor.notes && (
              <div className="mt-6 pt-4 border-t">
                <h3 className="text-sm font-medium text-gray-500 mb-2">Notes</h3>
                <p className="text-gray-700 whitespace-pre-wrap">{vendor.notes}</p>
              </div>
            )}
          </div>
          
          {/* Purchase Orders */}
          <div className="bg-white rounded-xl border p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <ShoppingBag className="w-5 h-5 text-gray-400" />
                Purchase Orders
                {stats?.po_count > 0 && (
                  <Badge variant="outline">{stats.po_count}</Badge>
                )}
              </h2>
              <Button size="sm" onClick={() => onCreatePO?.(vendor)} data-testid="create-po-btn">
                <Plus className="w-4 h-4 mr-1" />
                New PO
              </Button>
            </div>
            
            {purchaseOrders.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium">PO #</th>
                      <th className="px-3 py-2 text-left font-medium">Status</th>
                      <th className="px-3 py-2 text-right font-medium">Total</th>
                      <th className="px-3 py-2 text-left font-medium">Date</th>
                      <th className="px-3 py-2"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {purchaseOrders.map(po => (
                      <tr 
                        key={po.id} 
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={() => onNavigateToPO?.(po.id)}
                        data-testid={`po-row-${po.id}`}
                      >
                        <td className="px-3 py-2 font-mono font-medium">{po.po_number}</td>
                        <td className="px-3 py-2">
                          <Badge className={PO_STATUS_COLORS[po.status] || 'bg-gray-100'}>
                            {po.status}
                          </Badge>
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {formatCurrency(po.total_cents)}
                        </td>
                        <td className="px-3 py-2 text-gray-500">{formatDate(po.po_date)}</td>
                        <td className="px-3 py-2">
                          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); onNavigateToPO?.(po.id); }}>
                            <Eye className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <ShoppingBag className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No purchase orders yet</p>
              </div>
            )}
          </div>
          
          {/* Bills */}
          <div className="bg-white rounded-xl border p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <FileText className="w-5 h-5 text-gray-400" />
                Bills
                {stats?.open_bills_count > 0 && (
                  <Badge className="bg-yellow-100 text-yellow-700">{stats.open_bills_count} open</Badge>
                )}
              </h2>
              <Button size="sm" onClick={() => onCreateBill?.(vendor)} data-testid="create-bill-btn">
                <Plus className="w-4 h-4 mr-1" />
                New Bill
              </Button>
            </div>
            
            {bills.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium">Bill #</th>
                      <th className="px-3 py-2 text-left font-medium">Status</th>
                      <th className="px-3 py-2 text-right font-medium">Amount</th>
                      <th className="px-3 py-2 text-right font-medium">Balance</th>
                      <th className="px-3 py-2 text-left font-medium">Due</th>
                      <th className="px-3 py-2"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {bills.map(bill => (
                      <tr 
                        key={bill.id} 
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={() => onNavigateToBill?.(bill.id)}
                        data-testid={`bill-row-${bill.id}`}
                      >
                        <td className="px-3 py-2 font-mono font-medium">{bill.bill_number || bill.id.slice(0, 8)}</td>
                        <td className="px-3 py-2">
                          <Badge className={BILL_STATUS_COLORS[bill.status] || 'bg-gray-100'}>
                            {bill.status}
                          </Badge>
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">{formatCurrency(bill.amount_cents)}</td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          <span className={bill.balance_cents > 0 ? 'text-orange-600' : 'text-green-600'}>
                            {formatCurrency(bill.balance_cents)}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-gray-500">{formatDate(bill.due_date)}</td>
                        <td className="px-3 py-2">
                          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); onNavigateToBill?.(bill.id); }}>
                            <Eye className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No bills yet</p>
              </div>
            )}
          </div>

          {/* Checks Section */}
          <ChecksSection
            vendorId={vendorId}
            title="Checks"
            limit={5}
            emptyMessage="No checks issued to this vendor yet"
            onViewAll={() => {
              // Navigate to checks tab filtered by vendor
              window.location.href = `/admin?tab=checks&vendor=${vendorId}`;
            }}
          />
        </div>
        
        {/* Right Column - Stats & Quick Info */}
        <div className="space-y-6">
          {/* Stats Card */}
          <div className="bg-white rounded-xl border p-6">
            <h2 className="text-lg font-semibold mb-4">Summary</h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-500">Total POs</span>
                <span className="font-semibold">{stats?.po_count || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-500">Open Bills</span>
                <span className="font-semibold">{stats?.open_bills_count || 0}</span>
              </div>
              <div className="flex items-center justify-between border-t pt-4">
                <span className="text-gray-500">Outstanding Balance</span>
                <span className="font-semibold text-lg text-orange-600">
                  {formatCurrency(stats?.total_outstanding_cents || 0)}
                </span>
              </div>
            </div>
          </div>
          
          {/* Metadata Card */}
          <div className="bg-white rounded-xl border p-6">
            <h2 className="text-lg font-semibold mb-4">Details</h2>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Vendor ID</span>
                <span className="font-mono text-xs">{vendor.id}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Created</span>
                <span>{formatDate(vendor.created_at)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Last Updated</span>
                <span>{formatDate(vendor.updated_at)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Edit Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="edit-vendor-modal">
          <DialogHeader>
            <DialogTitle>Edit Vendor</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Basic Info */}
            <div>
              <Label htmlFor="name">Vendor Name *</Label>
              <Input
                id="name"
                value={editData.name || ''}
                onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                data-testid="edit-vendor-name"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="vendor_type">Type</Label>
                <Select
                  value={editData.vendor_type}
                  onValueChange={(v) => setEditData({ ...editData, vendor_type: v })}
                >
                  <SelectTrigger data-testid="edit-vendor-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {VENDOR_TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="payment_terms">Payment Terms</Label>
                <Select
                  value={editData.payment_terms || 'none'}
                  onValueChange={(v) => setEditData({ ...editData, payment_terms: v === 'none' ? '' : v })}
                >
                  <SelectTrigger data-testid="edit-payment-terms">
                    <SelectValue placeholder="Select terms" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {PAYMENT_TERMS.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* General Contact */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={editData.email || ''}
                  onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                  data-testid="edit-vendor-email"
                />
              </div>
              <div>
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={editData.phone || ''}
                  onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
                  data-testid="edit-vendor-phone"
                />
              </div>
            </div>
            
            {/* Primary Contact Section */}
            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-2">
                <Label>Primary Contact</Label>
                {editData.primary_contact_id ? (
                  <Button 
                    type="button" 
                    variant="ghost" 
                    size="sm"
                    onClick={handleUnlinkContact}
                    className="text-red-600"
                  >
                    <Unlink className="w-4 h-4 mr-1" />
                    Unlink
                  </Button>
                ) : (
                  <Button 
                    type="button" 
                    variant="ghost" 
                    size="sm"
                    onClick={() => setShowContactPicker(!showContactPicker)}
                  >
                    <LinkIcon className="w-4 h-4 mr-1" />
                    Link Contact
                  </Button>
                )}
              </div>
              
              {editData.primary_contact_id ? (
                <div className="bg-blue-50 p-3 rounded-lg">
                  <div className="flex items-center gap-2 text-blue-700">
                    <LinkIcon className="w-4 h-4" />
                    <span className="font-medium">Linked to Contact</span>
                  </div>
                  <div className="mt-2 text-sm">
                    <p className="font-medium">{editData.contact_name}</p>
                    <p className="text-gray-600">{editData.contact_email}</p>
                  </div>
                </div>
              ) : (
                <>
                  {showContactPicker && (
                    <div className="mb-3 p-3 bg-gray-50 rounded-lg">
                      <Input
                        placeholder="Search contacts..."
                        value={contactSearch}
                        onChange={(e) => {
                          setContactSearch(e.target.value);
                          searchContacts(e.target.value);
                        }}
                        className="mb-2"
                      />
                      {contacts.length > 0 && (
                        <div className="max-h-40 overflow-y-auto border rounded bg-white">
                          {contacts.map(c => (
                            <button
                              key={c.id}
                              type="button"
                              onClick={() => handleSelectContact(c)}
                              className="w-full text-left px-3 py-2 hover:bg-gray-50 border-b last:border-b-0"
                            >
                              <p className="font-medium">{c.first_name} {c.last_name}</p>
                              <p className="text-sm text-gray-500">{c.email}</p>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Manual entry fields */}
                  <div className="space-y-3">
                    <div>
                      <Label htmlFor="contact_name" className="text-sm">Contact Name</Label>
                      <Input
                        id="contact_name"
                        value={editData.contact_name || ''}
                        onChange={(e) => setEditData({ ...editData, contact_name: e.target.value })}
                        placeholder="Enter name manually"
                        data-testid="edit-contact-name"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label htmlFor="contact_email" className="text-sm">Contact Email</Label>
                        <Input
                          id="contact_email"
                          type="email"
                          value={editData.contact_email || ''}
                          onChange={(e) => setEditData({ ...editData, contact_email: e.target.value })}
                          placeholder="email@example.com"
                          data-testid="edit-contact-email"
                        />
                      </div>
                      <div>
                        <Label htmlFor="contact_phone" className="text-sm">Contact Phone</Label>
                        <Input
                          id="contact_phone"
                          value={editData.contact_phone || ''}
                          onChange={(e) => setEditData({ ...editData, contact_phone: e.target.value })}
                          placeholder="(555) 123-4567"
                          data-testid="edit-contact-phone"
                        />
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
            
            {/* Notes */}
            <div>
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={editData.notes || ''}
                onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
                rows={3}
                data-testid="edit-vendor-notes"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-vendor-btn">
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Add/Edit Contact Modal */}
      <Dialog open={showContactModal} onOpenChange={setShowContactModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingContact ? 'Edit Contact' : 'Add Contact'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="contact-name">Name *</Label>
              <Input
                id="contact-name"
                value={contactFormData.name}
                onChange={(e) => setContactFormData({ ...contactFormData, name: e.target.value })}
                placeholder="John Smith"
                data-testid="contact-name-input"
              />
            </div>
            
            <div>
              <Label htmlFor="contact-role">Role</Label>
              <Select
                value={contactFormData.role}
                onValueChange={(value) => setContactFormData({ ...contactFormData, role: value })}
              >
                <SelectTrigger data-testid="contact-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CONTACT_ROLES.map(role => (
                    <SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="contact-email">Email</Label>
              <Input
                id="contact-email"
                type="email"
                value={contactFormData.email}
                onChange={(e) => setContactFormData({ ...contactFormData, email: e.target.value })}
                placeholder="john@vendor.com"
                data-testid="contact-email-input"
              />
            </div>
            
            <div>
              <Label htmlFor="contact-phone">Phone</Label>
              <Input
                id="contact-phone"
                value={contactFormData.phone}
                onChange={(e) => setContactFormData({ ...contactFormData, phone: e.target.value })}
                placeholder="(555) 123-4567"
                data-testid="contact-phone-input"
              />
            </div>
            
            <div>
              <Label htmlFor="contact-notes">Notes</Label>
              <Textarea
                id="contact-notes"
                value={contactFormData.notes}
                onChange={(e) => setContactFormData({ ...contactFormData, notes: e.target.value })}
                placeholder="Any notes about this contact..."
                rows={2}
              />
            </div>
            
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="contact-is-primary"
                checked={contactFormData.is_primary}
                onChange={(e) => setContactFormData({ ...contactFormData, is_primary: e.target.checked })}
                className="rounded border-gray-300"
              />
              <Label htmlFor="contact-is-primary" className="font-normal cursor-pointer">
                Set as primary contact (shows on PO)
              </Label>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowContactModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveContact} disabled={savingContact} data-testid="save-contact-btn">
              {savingContact ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</>
              ) : editingContact ? 'Update Contact' : 'Add Contact'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VendorDetailPage;
